/* eslint-disable max-len */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DyslexiaScreenerContactModel } from 'src/types/DyslexiaScreenerContactModel';
import { DyslexiaScreenerFileListItemModel } from 'src/types/DyslexiaScreenerFileListItemModel';
import { DyslexiaScreenerFormViewModel } from 'src/types/DyslexiaScreenerFormViewModel';
import { DyslexiaScreenerListDisplayRowModel } from 'src/types/DyslexiaScreenerListDisplayRowModel';
import { DyslexiaScreenerModel } from 'src/types/DyslexiaScreenerModel';
import { SelectOption } from 'src/types/SelectOption';
import { DataEntryMode } from '../../types/DataEntryMode';
import {
  dyslexiaScreenerPendingFileUploadRef,
  formatDyslexiaScreenerFailedOrAtRiskPercentage,
  returnDyslexiaScreenerTrainingYearOrdinalInBlock,
} from '../../utilities/dyslexiaScreenerUtilities';
import { isEmptyObject } from '../../utilities/helperUtilities';
import type { RootState } from '../store';
import {
  createDyslexiaScreenerContact,
  deleteDyslexiaScreenerFileThunk,
  fetchDyslexiaScreenerById,
  fetchDyslexiaScreenerContact,
  fetchDyslexiaScreenerFilesBySchoolThunk,
  fetchDyslexiaScreenerFormViewModel,
  fetchDyslexiaScreenerLeas,
  fetchDyslexiaScreenerListItems,
  fetchDyslexiaScreenerSchoolsByLeaId,
  fetchDyslexiaScreenerSchoolYears,
  updateDyslexiaScreenerContact,
  uploadDyslexiaScreenerFileThunk,
} from '../thunks/dyslexiaScreenerThunks';

export type DyslexiaScreenerPendingListRowContext = Pick<
  DyslexiaScreenerModel,
  | 'leaIdentifier'
  | 'districtName'
  | 'schoolIdentifier'
  | 'schoolName'
  | 'schoolYear'
  | 'kgIEPStudentCount'
  | 'grade1IEPStudentCount'
  | 'kg504StudentCount'
  | 'grade1504StudentCount'
  | 'kgExists'
  | 'grade1Exists'
>;

export interface DyslexiaScreenerListFiltersSnapshot {
  lea: string;
  school: string;
  type: string;
  status: string;
}

/** No document type pre-selected until the user chooses one. */
const defaultDyslexiaScreenerSelectedDocumentUploadType = '';

interface DyslexiaScreenerState {
  loadingListItems: boolean;
  listItems: DyslexiaScreenerListDisplayRowModel[];
  dyslexiaScreenerUpdate: boolean;
  loadingDetail: boolean;
  detail: DyslexiaScreenerModel;
  pendingListRowContext: DyslexiaScreenerPendingListRowContext | null;
  loadingFormViewModel: boolean;
  formViewModel: DyslexiaScreenerFormViewModel;
  dataEntryMode: DataEntryMode;
  loadingContact: boolean;
  contact: DyslexiaScreenerContactModel | null;
  loadingSchoolYears: boolean;
  schoolYears: SelectOption[];
  loadingLeas: boolean;
  leas: SelectOption[];
  loadingSchools: boolean;
  schools: SelectOption[];
  error: string;
  dyslexiaScreenerFiles: DyslexiaScreenerFileListItemModel[];
  dyslexiaScreenerSelectedDocumentUploadType: string;
  /** Mirrors new-staff Yes/No from Awareness Training panel for document upload rules. */
  dyslexiaScreenerPanelNewStaffTrainingRequired: string;
  loadingDyslexiaScreenerFiles: boolean;
  uploadingDyslexiaScreenerFile: boolean;
  deletingDyslexiaScreenerFile: boolean;
  listFiltersSnapshot: DyslexiaScreenerListFiltersSnapshot | null;
}

export const initialContactFormValues: DyslexiaScreenerContactModel = {
  id: '',
  leaIdentifier: '',
  firstName: '',
  lastOrSurname: '',
  email: '',
  telephone: '',
};

const defaultListState: DyslexiaScreenerListDisplayRowModel[] = [];

export const defaultDyslexiaScreenerFormViewModel: DyslexiaScreenerFormViewModel =
  {
    dyslexiaScreenerAssementOptions: [],
  };

export const defaultDyslexiaScreenerModel: DyslexiaScreenerModel = {
  id: '',
  schoolYear: 0,
  displaySchoolPanels: false,
  leaIdentifier: '',
  districtName: '',
  schoolIdentifier: '',
  schoolName: '',
  kgScreenerUsed: '',
  kgScreenerCompletionDate: '',
  kgScreenerCount: 0,
  kgScreenerFailedOrAtRiskCount: 0,
  kgParentsNotified: false,
  grade1ScreenerUsed: '',
  grade1ScreenerCompletionDate: '',
  grade1ScreenerCount: 0,
  grade1ScreenerFailedOrAtRiskCount: 0,
  grade1ParentsNotified: false,
  grade1IEPStudentCount: 0,
  grade1504StudentCount: 0,
  kgIEPStudentCount: 0,
  kg504StudentCount: 0,
  grade1FailedOrAtRiskPercentage: '',
  kgFailedOrAtRiskPercentage: '',
  trainingCycle: 0,
  trainingAcknowledgementDate: '',
  trainingCompleted: false,
  trainingUsed: '',
  otherTrainingUsed: '',
  otherTrainingAcknowldegment: false,
};

const toOtherTrainingAcknowledgmentBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.trim().length > 0;
  return false;
};

const asOptionalNumber = (v: unknown): number | undefined => {
  if (v === null || v === undefined) return undefined;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && String(v).trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
};

const firstDefinedNumber = (...vals: unknown[]): number => {
  for (const v of vals) {
    const n = asOptionalNumber(v);
    if (n !== undefined) return n;
  }
  return 0;
};

const rawProp = (model: DyslexiaScreenerModel, key: string): unknown =>
  (model as unknown as Record<string, unknown>)[key];

const normalizeDyslexiaScreenerModel = (
  model: DyslexiaScreenerModel
): DyslexiaScreenerModel => {
  const apiModel = model as DyslexiaScreenerModel & {
    kgiepStudentCount?: number;
    kgiEPStudentCount?: number;
    grade1iepStudentCount?: number;
    kG504StudentCount?: number;
    kg504studentCount?: number;
    grade1504studentCount?: number;
    grede1504StudentCount?: number;
  };

  const kgIEPStudentCount = firstDefinedNumber(
    model.kgIEPStudentCount,
    rawProp(model, 'kgiepStudentCount'),
    apiModel.kgiepStudentCount,
    apiModel.kgiEPStudentCount
  );
  const grade1IEPStudentCount = firstDefinedNumber(
    model.grade1IEPStudentCount,
    rawProp(model, 'grade1IEPStudentCount'),
    apiModel.grade1iepStudentCount
  );
  const kg504StudentCount = firstDefinedNumber(
    model.kg504StudentCount,
    rawProp(model, 'kG504StudentCount'),
    apiModel.kG504StudentCount,
    apiModel.kg504studentCount
  );
  const grade1504StudentCount = firstDefinedNumber(
    model.grade1504StudentCount,
    rawProp(model, 'grade1504StudentCount'),
    apiModel.grade1504studentCount,
    apiModel.grede1504StudentCount
  );

  return {
    ...model,
    kgIEPStudentCount,
    grade1IEPStudentCount,
    kg504StudentCount,
    grade1504StudentCount,
    grade1FailedOrAtRiskPercentage:
      formatDyslexiaScreenerFailedOrAtRiskPercentage(
        model.grade1ScreenerCount,
        model.grade1ScreenerFailedOrAtRiskCount
      ),
    kgFailedOrAtRiskPercentage: formatDyslexiaScreenerFailedOrAtRiskPercentage(
      model.kgScreenerCount,
      model.kgScreenerFailedOrAtRiskCount
    ),
    otherTrainingAcknowldegment: toOtherTrainingAcknowledgmentBoolean(
      model.otherTrainingAcknowldegment
    ),
    trainingCycle: returnDyslexiaScreenerTrainingYearOrdinalInBlock(
      model.schoolYear
    ),
  };
};

const applyPendingListRowContext = (
  detail: DyslexiaScreenerModel,
  ctx: DyslexiaScreenerPendingListRowContext
): DyslexiaScreenerModel => {
  const withLocation = {
    ...detail,
    leaIdentifier: ctx.leaIdentifier,
    districtName: ctx.districtName,
    schoolIdentifier: ctx.schoolIdentifier,
    schoolName: ctx.schoolName,
    schoolYear: ctx.schoolYear,
    ...(ctx.kgExists !== undefined ? { kgExists: ctx.kgExists } : {}),
    ...(ctx.grade1Exists !== undefined
      ? { grade1Exists: ctx.grade1Exists }
      : {}),
  };
  const hasRecordId = (detail.id ?? '').trim() !== '';
  if (hasRecordId) {
    return normalizeDyslexiaScreenerModel(withLocation);
  }
  return normalizeDyslexiaScreenerModel({
    ...withLocation,
    kgIEPStudentCount: ctx.kgIEPStudentCount,
    grade1IEPStudentCount: ctx.grade1IEPStudentCount,
    kg504StudentCount: ctx.kg504StudentCount,
    grade1504StudentCount: ctx.grade1504StudentCount,
  });
};

export const initialState: DyslexiaScreenerState = {
  loadingListItems: false,
  listItems: defaultListState,
  dyslexiaScreenerUpdate: false,
  loadingDetail: false,
  detail: defaultDyslexiaScreenerModel,
  pendingListRowContext: null,
  loadingFormViewModel: false,
  formViewModel: defaultDyslexiaScreenerFormViewModel,
  dataEntryMode: DataEntryMode.NEW,
  loadingContact: false,
  contact: initialContactFormValues,
  loadingSchoolYears: false,
  schoolYears: [],
  loadingLeas: false,
  leas: [],
  loadingSchools: false,
  schools: [],
  error: '',
  dyslexiaScreenerFiles: [],
  dyslexiaScreenerSelectedDocumentUploadType:
    defaultDyslexiaScreenerSelectedDocumentUploadType,
  dyslexiaScreenerPanelNewStaffTrainingRequired: '',
  loadingDyslexiaScreenerFiles: false,
  uploadingDyslexiaScreenerFile: false,
  deletingDyslexiaScreenerFile: false,
  listFiltersSnapshot: null,
};

export const dyslexiaScreenerSlice = createSlice({
  name: 'dyslexiaScreener',
  initialState,
  reducers: {
    clearDyslexiaScreenerDetail: (state) => {
      dyslexiaScreenerPendingFileUploadRef.hasPendingFile = false;
      state.detail = defaultDyslexiaScreenerModel;
      state.dyslexiaScreenerFiles = [];
      state.dyslexiaScreenerSelectedDocumentUploadType =
        defaultDyslexiaScreenerSelectedDocumentUploadType;
      state.dyslexiaScreenerPanelNewStaffTrainingRequired = '';
      state.loadingDyslexiaScreenerFiles = false;
      state.uploadingDyslexiaScreenerFile = false;
      state.deletingDyslexiaScreenerFile = false;
      const ctx = state.pendingListRowContext;
      if (ctx) {
        state.detail = applyPendingListRowContext(
          { ...defaultDyslexiaScreenerModel, id: '' },
          ctx
        );
        // Keep pendingListRowContext until save or fetch settles so a second
        // clear (e.g. React Strict Mode remount) can merge again instead of
        // leaving an empty NEW form.
      }
    },
    setDyslexiaScreenerPendingListRowContext: (
      state,
      action: PayloadAction<DyslexiaScreenerPendingListRowContext>
    ) => {
      state.pendingListRowContext = action.payload;
    },
    clearDyslexiaScreenerPendingListRowContext: (state) => {
      state.pendingListRowContext = null;
    },
    setDyslexiaScreenerListFiltersSnapshot: (
      state,
      action: PayloadAction<DyslexiaScreenerListFiltersSnapshot>
    ) => {
      state.listFiltersSnapshot = action.payload;
    },
    clearDyslexiaScreenerListFiltersSnapshot: (state) => {
      state.listFiltersSnapshot = null;
    },
    saveDyslexiaScreener: (
      state,
      action: PayloadAction<DyslexiaScreenerModel>
    ) => {
      state.detail = normalizeDyslexiaScreenerModel(action.payload);
      state.pendingListRowContext = null;
    },
    setDyslexiaScreenerDataEntryMode: (
      state,
      action: PayloadAction<DataEntryMode>
    ) => {
      state.dataEntryMode = action.payload;
    },
    setDyslexiaScreenerSelectedDocumentUploadType: (
      state,
      action: PayloadAction<string>
    ) => {
      state.dyslexiaScreenerSelectedDocumentUploadType = action.payload;
    },
    setDyslexiaScreenerPanelNewStaffTrainingRequired: (
      state,
      action: PayloadAction<string>
    ) => {
      state.dyslexiaScreenerPanelNewStaffTrainingRequired = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchDyslexiaScreenerById.pending, (state) => {
      state.detail = defaultDyslexiaScreenerModel;
      state.loadingDetail = true;
      state.error = '';
      state.dyslexiaScreenerFiles = [];
      state.dyslexiaScreenerSelectedDocumentUploadType =
        defaultDyslexiaScreenerSelectedDocumentUploadType;
      state.dyslexiaScreenerPanelNewStaffTrainingRequired = '';
      state.loadingDyslexiaScreenerFiles = false;
    });
    builder.addCase(fetchDyslexiaScreenerById.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        let detail = normalizeDyslexiaScreenerModel(action.payload);
        const ctx = state.pendingListRowContext;
        if (ctx) {
          detail = applyPendingListRowContext(detail, ctx);
          state.pendingListRowContext = null;
        }
        state.detail = detail;
      } else {
        state.pendingListRowContext = null;
      }
      state.loadingDetail = false;
    });
    builder.addCase(fetchDyslexiaScreenerById.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingDetail = false;
      }
      state.pendingListRowContext = null;
      state.error =
        action.error.message || 'Failed to fetch dyslexia screener detail';
    });

    builder.addCase(fetchDyslexiaScreenerFormViewModel.pending, (state) => {
      state.formViewModel = defaultDyslexiaScreenerFormViewModel;
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchDyslexiaScreenerFormViewModel.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.formViewModel = action.payload;
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchDyslexiaScreenerFormViewModel.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    builder.addCase(fetchDyslexiaScreenerListItems.pending, (state) => {
      state.listItems = defaultListState;
      state.dyslexiaScreenerUpdate = false;
      state.loadingListItems = true;
      state.error = '';
    });
    builder.addCase(
      fetchDyslexiaScreenerListItems.fulfilled,
      (state, action) => {
        state.listItems = action.payload.displayRows;
        state.dyslexiaScreenerUpdate = action.payload.dyslexiaScreenerUpdate;
        state.loadingListItems = false;
      }
    );
    builder.addCase(
      fetchDyslexiaScreenerListItems.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingListItems = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch dyslexia screener list items';
      }
    );

    builder.addCase(fetchDyslexiaScreenerContact.pending, (state, action) => {
      const showLoading = action.meta.arg?.showLoading !== false;
      if (showLoading) {
        state.loadingContact = true;
      }
      state.error = '';
    });
    builder.addCase(fetchDyslexiaScreenerContact.fulfilled, (state, action) => {
      if ('data' in action.payload) {
        state.contact = action.payload.data;
      } else {
        state.contact = { ...initialContactFormValues };
      }
      state.loadingContact = false;
    });
    builder.addCase(fetchDyslexiaScreenerContact.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingContact = false;
      }
      state.error =
        action.error.message || 'Failed to fetch dyslexia screener contact';
    });

    builder.addCase(fetchDyslexiaScreenerSchoolYears.pending, (state) => {
      state.schoolYears = [];
      state.loadingSchoolYears = true;
      state.error = '';
    });
    builder.addCase(
      fetchDyslexiaScreenerSchoolYears.fulfilled,
      (state, action) => {
        state.schoolYears = action.payload;
        state.loadingSchoolYears = false;
      }
    );
    builder.addCase(
      fetchDyslexiaScreenerSchoolYears.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingSchoolYears = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch dyslexia screener school years';
      }
    );

    builder.addCase(fetchDyslexiaScreenerLeas.pending, (state) => {
      state.leas = [];
      state.loadingLeas = true;
      state.error = '';
    });
    builder.addCase(fetchDyslexiaScreenerLeas.fulfilled, (state, action) => {
      state.leas = action.payload;
      state.loadingLeas = false;
    });
    builder.addCase(fetchDyslexiaScreenerLeas.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingLeas = false;
      }
      state.error =
        action.error.message || 'Failed to fetch dyslexia screener LEAs';
    });

    builder.addCase(fetchDyslexiaScreenerSchoolsByLeaId.pending, (state) => {
      state.schools = [];
      state.loadingSchools = true;
      state.error = '';
    });
    builder.addCase(
      fetchDyslexiaScreenerSchoolsByLeaId.fulfilled,
      (state, action) => {
        state.schools = action.payload;
        state.loadingSchools = false;
      }
    );
    builder.addCase(
      fetchDyslexiaScreenerSchoolsByLeaId.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingSchools = false;
        }
        state.error =
          action.error.message || 'Failed to fetch dyslexia screener schools';
      }
    );

    builder.addCase(updateDyslexiaScreenerContact.pending, (state) => {
      state.error = '';
    });
    builder.addCase(updateDyslexiaScreenerContact.fulfilled, (state) => {
      state.error = '';
    });
    builder.addCase(updateDyslexiaScreenerContact.rejected, (state, action) => {
      state.error =
        action.error.message || 'Failed to update dyslexia screener contact';
    });

    builder.addCase(createDyslexiaScreenerContact.pending, (state) => {
      state.error = '';
    });
    builder.addCase(createDyslexiaScreenerContact.fulfilled, (state) => {
      state.error = '';
    });
    builder.addCase(createDyslexiaScreenerContact.rejected, (state, action) => {
      state.error =
        action.error.message || 'Failed to create dyslexia screener contact';
    });

    builder.addCase(uploadDyslexiaScreenerFileThunk.pending, (state) => {
      state.uploadingDyslexiaScreenerFile = true;
    });
    builder.addCase(uploadDyslexiaScreenerFileThunk.fulfilled, (state) => {
      state.uploadingDyslexiaScreenerFile = false;
    });
    builder.addCase(
      uploadDyslexiaScreenerFileThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.uploadingDyslexiaScreenerFile = false;
        }
      }
    );

    builder.addCase(deleteDyslexiaScreenerFileThunk.pending, (state) => {
      state.deletingDyslexiaScreenerFile = true;
      state.error = '';
    });
    builder.addCase(deleteDyslexiaScreenerFileThunk.fulfilled, (state) => {
      state.deletingDyslexiaScreenerFile = false;
    });
    builder.addCase(
      deleteDyslexiaScreenerFileThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.deletingDyslexiaScreenerFile = false;
        }
        state.error =
          action.error.message || 'Failed to delete dyslexia screener file';
      }
    );

    builder.addCase(
      fetchDyslexiaScreenerFilesBySchoolThunk.pending,
      (state, action) => {
        const showLoading = action.meta.arg?.showLoading !== false;
        if (showLoading) {
          state.loadingDyslexiaScreenerFiles = true;
        }
        state.error = '';
      }
    );
    builder.addCase(
      fetchDyslexiaScreenerFilesBySchoolThunk.fulfilled,
      (state, action) => {
        state.dyslexiaScreenerFiles = action.payload || [];
        state.loadingDyslexiaScreenerFiles = false;
      }
    );
    builder.addCase(
      fetchDyslexiaScreenerFilesBySchoolThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingDyslexiaScreenerFiles = false;
        }
        state.error =
          action.error.message ||
          'Failed to fetch dyslexia screener uploaded files';
      }
    );
  },
});

export const selectDyslexiaScreenerListUpdateAllowed = (
  state: RootState
): boolean => state.dyslexiaScreener.dyslexiaScreenerUpdate;

export const selectDyslexiaScreenerListItems = (
  state: RootState
): DyslexiaScreenerListDisplayRowModel[] => state.dyslexiaScreener.listItems;

export const selectDyslexiaScreenerLoadingListItems = (
  state: RootState
): boolean => state.dyslexiaScreener.loadingListItems;

export const selectDyslexiaScreenerContact = (
  state: RootState
): DyslexiaScreenerContactModel | null => state.dyslexiaScreener.contact;

export const selectDyslexiaScreenerLoadingContact = (
  state: RootState
): boolean => state.dyslexiaScreener.loadingContact;

export const selectDyslexiaScreenerSchoolYears = (
  state: RootState
): SelectOption[] => state.dyslexiaScreener.schoolYears;

export const selectDyslexiaScreenerLoadingSchoolYears = (
  state: RootState
): boolean => state.dyslexiaScreener.loadingSchoolYears;

export const selectDyslexiaScreenerLeas = (state: RootState): SelectOption[] =>
  state.dyslexiaScreener.leas;

export const selectDyslexiaScreenerLoadingLeas = (state: RootState): boolean =>
  state.dyslexiaScreener.loadingLeas;

export const selectDyslexiaScreenerSchools = (
  state: RootState
): SelectOption[] => state.dyslexiaScreener.schools;

export const selectDyslexiaScreenerLoadingSchools = (
  state: RootState
): boolean => state.dyslexiaScreener.loadingSchools;

export const selectDyslexiaScreenerFiles = (
  state: RootState
): DyslexiaScreenerFileListItemModel[] =>
  state.dyslexiaScreener.dyslexiaScreenerFiles;

export const selectDyslexiaScreenerSelectedDocumentUploadType = (
  state: RootState
): string => state.dyslexiaScreener.dyslexiaScreenerSelectedDocumentUploadType;

export const selectDyslexiaScreenerPanelNewStaffTrainingRequired = (
  state: RootState
): string =>
  state.dyslexiaScreener.dyslexiaScreenerPanelNewStaffTrainingRequired;

export const selectDyslexiaScreenerLoadingFiles = (state: RootState): boolean =>
  state.dyslexiaScreener.loadingDyslexiaScreenerFiles;

export const selectDyslexiaScreenerUploadingFile = (
  state: RootState
): boolean => state.dyslexiaScreener.uploadingDyslexiaScreenerFile;

export const selectDyslexiaScreenerDeletingFile = (state: RootState): boolean =>
  state.dyslexiaScreener.deletingDyslexiaScreenerFile;

export const selectDyslexiaScreenerDetail = (
  state: RootState
): DyslexiaScreenerModel => state.dyslexiaScreener.detail;

export const selectDyslexiaScreenerPendingListRowContext = (
  state: RootState
): DyslexiaScreenerPendingListRowContext | null =>
  state.dyslexiaScreener.pendingListRowContext;

export const selectDyslexiaScreenerListFiltersSnapshot = (
  state: RootState
): DyslexiaScreenerListFiltersSnapshot | null =>
  state.dyslexiaScreener.listFiltersSnapshot;

export const selectDyslexiaScreenerFormViewModel = (
  state: RootState
): DyslexiaScreenerFormViewModel => state.dyslexiaScreener.formViewModel;

export const selectDyslexiaScreenerLoadingFormViewModel = (
  state: RootState
): boolean => state.dyslexiaScreener.loadingFormViewModel;

export const selectDyslexiaScreenerLoadingDetail = (
  state: RootState
): boolean => state.dyslexiaScreener.loadingDetail;

export const selectDyslexiaScreenerDataEntryMode = (
  state: RootState
): DataEntryMode => state.dyslexiaScreener.dataEntryMode;

export const {
  clearDyslexiaScreenerDetail,
  clearDyslexiaScreenerListFiltersSnapshot,
  clearDyslexiaScreenerPendingListRowContext,
  saveDyslexiaScreener,
  setDyslexiaScreenerDataEntryMode,
  setDyslexiaScreenerListFiltersSnapshot,
  setDyslexiaScreenerPanelNewStaffTrainingRequired,
  setDyslexiaScreenerPendingListRowContext,
  setDyslexiaScreenerSelectedDocumentUploadType,
} = dyslexiaScreenerSlice.actions;
